import { WSLoader } from "@wingspanhq/fe-component-library";
import { useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryCustomerEntity } from "../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryMemberProfile } from "../../../query/users/queries";
import { FormManuallyAddAccount } from "../../../shared/components/FormManuallyAddAccount";
import { FormType } from "../../../shared/components/FormManuallyAddAccount/FormType";
import {
  ManualAccountSubType,
  ManualAccountType
} from "../../../shared/components/FormManuallyAddAccount/types";
import { FormManuallyAddInternationalAccount } from "../../../shared/components/FormManuallyAddInternationalAccount";
import { Layout } from "../../../shared/components/Layout";
import { StepLayoutWithOptions } from "../../../shared/components/Layout/StepLayoutWithOptions";
import {
  avatarBankAccount,
  avatarInternationalBankAccount,
  avatarPlaid,
  avatarUSBankAccount
} from "../../../shared/constants/avatars";
import { useExternalBankAccountsPlaidLink } from "../../../shared/hooks/useExternalBankAccountsPlaidLink";
import { selectorUseInternationalPayouts } from "../../../shared/selectors/selectorUseInternationalPayouts";
import { PATH_TRANSFER, PATH_TRANSFER_ADD_BANK_ACCOUNT } from "../paths";

export const RouteAddBankAccount = () => {
  const history = useHistory();
  const location = useLocation();

  const basePath = PATH_TRANSFER_ADD_BANK_ACCOUNT;

  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);
  const queryCustomerEntity = useQueryCustomerEntity();
  const isInternational = queryMember.data
    ? selectorUseInternationalPayouts(
        queryMember.data,
        queryCustomerEntity.data
      )
    : false;

  const searchParams = new URLSearchParams(location.search);
  const noManualAdd = searchParams.get("type") === "deposit";

  const onBack = () => {
    history.push(PATH_TRANSFER + location.search);
  };

  const [manualAccountType, setManualAccountType] = useState<{
    type: ManualAccountType;
    subType: ManualAccountSubType;
  }>({
    type: "personal",
    subType: "checking"
  });

  const plaidLink = useExternalBankAccountsPlaidLink({
    onSuccess: onBack
  });

  if (queryMember.isLoading || queryCustomerEntity.isLoading) {
    return (
      <Layout
        title="Add bank account"
        onClose={onBack}
        showFooterAdditionalInfo
      >
        <WSLoader.Spinner />
      </Layout>
    );
  }

  return (
    <Layout title="Add bank account" onClose={onBack} showFooterAdditionalInfo>
      <Switch>
        <Route path={basePath + "/type"}>
          <StepLayoutWithOptions<"international" | "us">
            key="type"
            title="Add bank account"
            options={[
              {
                header: {
                  label: {
                    text: "Non-U.S. account",
                    helperText:
                      "Wingspan will deposit into your bank account in the currency of that account in a non-U.S. country or in U.S.-dollars for USD accounts.",
                    avatar: avatarInternationalBankAccount
                  }
                },
                value: "international"
              },
              {
                header: {
                  label: {
                    text: "U.S. based account",
                    helperText:
                      "Wingspan will keep your deposits in USD and deposit it in your U.S.-based account.",

                    avatar: avatarUSBankAccount
                  }
                },
                value: "us"
              }
            ].filter(option => {
              if (noManualAdd && option.value === "international") {
                return false;
              }
              return true;
            })}
            onBack={onBack}
            onNext={value => {
              history.push(basePath + "/" + value + location.search);
            }}
          />
        </Route>

        <Route path={basePath + "/us"}>
          <StepLayoutWithOptions<"plaid" | "manual-type">
            key="type"
            title="Add bank account"
            options={[
              {
                header: {
                  label: {
                    text: "Link account with Plaid",
                    helperText:
                      "Instantly verify and connect your bank accounts securely",
                    avatar: avatarPlaid
                  }
                },
                value: "plaid"
              },
              {
                header: {
                  label: {
                    text: "Set up account manually",
                    helperText:
                      "Manually enter your routing and account numbers",
                    avatar: avatarBankAccount
                  }
                },
                value: "manual-type"
              }
            ].filter(option => {
              if (noManualAdd && option.value === "manual-type") {
                return false;
              }
              return true;
            })}
            onBack={onBack}
            onNext={value => {
              if (value === "plaid") {
                plaidLink.open();
              } else {
                history.push(basePath + "/" + value + location.search);
              }
            }}
          />
        </Route>

        <Route path={basePath + "/international"}>
          <FormManuallyAddInternationalAccount
            onBack={() => {
              history.push(basePath + "/type" + location.search);
            }}
            onSuccess={onBack}
            withPanel
            skipPayoutSettingsUpdate
          />
        </Route>

        <Route path={basePath + "/manual-type"}>
          <FormType
            value={manualAccountType}
            onBack={() => {
              history.push(basePath + "/us" + location.search);
            }}
            onNext={newValue => {
              setManualAccountType(newValue);
              history.push(basePath + "/manual" + location.search);
            }}
          />
        </Route>

        <Route path={basePath + "/manual"}>
          <FormManuallyAddAccount
            onBack={() => {
              history.push(basePath + "/manual-type" + location.search);
            }}
            onSuccess={onBack}
            withPanel
            type={manualAccountType.type}
            subType={manualAccountType.subType}
          />
        </Route>

        <Redirect
          from={basePath}
          to={
            isInternational
              ? basePath + "/type" + location.search
              : basePath + "/us" + location.search
          }
        />
      </Switch>
    </Layout>
  );
};
